import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "./ui/button";
import { Cookie, X, Settings, Check } from "lucide-react";

const COOKIE_CONSENT_KEY = "erems_cookie_consent";

export default function CookieConsent() {
  const { t } = useTranslation();
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Always true, can't be disabled
    analytics: true,
    functional: true,
  });

  useEffect(() => {
    // Check if user has already given consent
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!consent) {
      // Small delay before showing banner for better UX
      const timer = setTimeout(() => setShowBanner(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAcceptAll = () => {
    const consent = {
      necessary: true,
      analytics: true,
      functional: true,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(consent));
    setShowBanner(false);
  };

  const handleAcceptSelected = () => {
    const consent = {
      ...preferences,
      necessary: true,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(consent));
    setShowBanner(false);
  };

  const handleRejectNonEssential = () => {
    const consent = {
      necessary: true,
      analytics: false,
      functional: false,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(consent));
    setShowBanner(false);
  };

  if (!showBanner) return null;

  return (
    <div 
      className="fixed bottom-0 left-0 right-0 z-[100] p-4 md:p-6 animate-in slide-in-from-bottom duration-500"
      data-testid="cookie-consent-banner"
    >
      <div className="max-w-4xl mx-auto bg-card border border-border rounded-xl shadow-2xl overflow-hidden">
        {/* Main Banner */}
        <div className="p-4 md:p-6">
          <div className="flex items-start gap-4">
            <div className="hidden sm:flex items-center justify-center w-12 h-12 rounded-full bg-emerald-500/10 shrink-0">
              <Cookie className="w-6 h-6 text-emerald-500" />
            </div>
            
            <div className="flex-1 min-w-0">
              <h3 className="text-lg font-semibold mb-2">{t('cookieConsent.title')}</h3>
              <p className="text-sm text-muted-foreground mb-4">
                {t('cookieConsent.description')}{" "}
                <Link 
                  to="/cookie-policy" 
                  className="text-emerald-400 hover:underline"
                  data-testid="cookie-policy-link"
                >
                  {t('footerComp.cookie')}
                </Link>.
              </p>

              {/* Cookie Settings Panel */}
              {showSettings && (
                <div className="mb-4 p-4 bg-muted/50 rounded-lg space-y-3 animate-in fade-in duration-300">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-sm">{t('cookieConsent.necessary')}</p>
                      <p className="text-xs text-muted-foreground">{t('cookieConsent.necessaryDesc')}</p>
                    </div>
                    <div className="flex items-center gap-2 text-emerald-500">
                      <Check className="w-4 h-4" />
                      <span className="text-xs">{t('cookieConsent.alwaysOn')}</span>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-sm">{t('cookieConsent.analytics')}</p>
                      <p className="text-xs text-muted-foreground">{t('cookieConsent.analyticsDesc')}</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={preferences.analytics}
                        onChange={(e) => setPreferences(prev => ({ ...prev, analytics: e.target.checked }))}
                        className="sr-only peer"
                        data-testid="analytics-toggle"
                      />
                      <div className="w-9 h-5 bg-muted-foreground/30 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-emerald-500"></div>
                    </label>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-sm">{t('cookieConsent.functional')}</p>
                      <p className="text-xs text-muted-foreground">{t('cookieConsent.functionalDesc')}</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={preferences.functional}
                        onChange={(e) => setPreferences(prev => ({ ...prev, functional: e.target.checked }))}
                        className="sr-only peer"
                        data-testid="functional-toggle"
                      />
                      <div className="w-9 h-5 bg-muted-foreground/30 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-emerald-500"></div>
                    </label>
                  </div>
                </div>
              )}

              {/* Buttons */}
              <div className="flex flex-wrap gap-2">
                <Button
                  onClick={handleAcceptAll}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="accept-all-cookies"
                >
                  {t('cookieConsent.acceptAll')}
                </Button>
                
                {showSettings ? (
                  <Button
                    onClick={handleAcceptSelected}
                    variant="outline"
                    data-testid="save-preferences"
                  >
                    {t('cookieConsent.save')}
                  </Button>
                ) : (
                  <Button
                    onClick={() => setShowSettings(true)}
                    variant="outline"
                    className="gap-2"
                    data-testid="customize-cookies"
                  >
                    <Settings className="w-4 h-4" />
                    {t('cookieConsent.customize')}
                  </Button>
                )}
                
                <Button
                  onClick={handleRejectNonEssential}
                  variant="ghost"
                  className="text-muted-foreground hover:text-foreground"
                  data-testid="reject-non-essential"
                >
                  {t('cookieConsent.essentialOnly')}
                </Button>
              </div>
            </div>

            {/* Close button */}
            <button
              onClick={handleRejectNonEssential}
              className="text-muted-foreground hover:text-foreground transition-colors shrink-0"
              aria-label={t('cookieConsent.close')}
              data-testid="close-cookie-banner"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
